// apps/lumen/src/features/calendar/CalendarEmptyState.tsx
import { cva } from 'class-variance-authority';
import { Flex, Text, Button, Icon, iconWeight, message } from '@nula/core-ui';
import { ApiError } from '@/lib/http/client';
import { openCalendarOAuth } from '@/lib/calendar/calendar-oauth';
import styles from './calendar-empty-state.module.css';

const emptyState = cva(styles.base);

interface CalendarEmptyStateProps {
  onConnected: () => void;
}

export function CalendarEmptyState({ onConnected: _onConnected }: CalendarEmptyStateProps) {
  const handleConnect = async (provider: 'google' | 'microsoft') => {
    try {
      await openCalendarOAuth(provider);
    } catch (err) {
      // Pre-fix this catch was empty, so any failure (auth expired,
      // wrong API URL, opener plugin denied, network) silently
      // produced a "click does nothing" UX. Surface the real reason.
      console.error('[CalendarEmptyState] openCalendarOAuth failed', err);
      const detail =
        err instanceof ApiError
          ? `${err.code} (${err.status})`
          : err instanceof Error
            ? err.message
            : String(err);
      message.error(`Couldn't open calendar OAuth: ${detail}`);
    }
  };

  return (
    <Flex
      vertical
      align="center"
      justify="center"
      gap="lg"
      className={emptyState()}
    >
      <Icon name="calendar_month" size={32} weight={iconWeight.light} className={styles.icon} />
      <Flex vertical align="center" gap="sm">
        <Text className={styles.title}>
          Connect your calendar
        </Text>
        <Text size="sm" type="secondary" className={styles.subtitle}>
          Sync your Google or Microsoft calendar to see events and link them to your notes.
        </Text>
      </Flex>
      <Flex gap="md">
        <Button
          variant="default"
          icon={<Icon name="event" size={16} />}
          onClick={() => handleConnect('google')}
        >
          Connect Google Calendar
        </Button>
        <Button
          variant="default"
          icon={<Icon name="event" size={16} />}
          onClick={() => handleConnect('microsoft')}
        >
          Connect Microsoft Calendar
        </Button>
      </Flex>
    </Flex>
  );
}
